import React, { useMemo } from "react";
import { LayersControl, TileLayer } from "react-leaflet";

import { OpenFreeMapLayer } from "./OpenFreeMapLayer";

type LayerName = "Basic" | "Dark" | "Satellite" | "Standard" | "Topographic";

type LayerProps = {
  maxNativeZoom?: number;
  maxZoom?: number;
};

export const MapLayers = ({
  defaultLayer,
  layerProps,
  layers,
}: {
  defaultLayer?: LayerName;
  layerProps?: LayerProps | undefined;
  layers: LayerName[] | undefined;
}) => {
  const allLayers = useMemo(() => {
    return {
      Basic: <BasicTileLayer {...(layerProps ?? {})} />,
      Dark: <DarkThemedTileLayer {...(layerProps ?? {})} />,
      Satellite: <SatelliteTileLayer {...(layerProps ?? {})} />,
      Standard: <StandardTileLayer {...(layerProps ?? {})} />,
      Topographic: <TopographicTileLayer {...(layerProps ?? {})} />,
    };
  }, [layerProps]);

  if (!layers) return null;
  return (
    <LayersControl position="bottomleft">
      {layers
        ?.filter((layer) => Object.keys(allLayers).includes(layer))
        .map((layer, i) => (
          <LayersControl.BaseLayer checked={!defaultLayer ? i === 0 : layer === defaultLayer} key={i} name={layer}>
            {allLayers[layer]}
          </LayersControl.BaseLayer>
        ))}
    </LayersControl>
  );
};

const BasicTileLayer = (props: LayerProps) => {
  return <OpenFreeMapLayer styleUrl="https://tiles.openfreemap.org/styles/liberty" {...props} />;
};

const StandardTileLayer = (props: LayerProps) => {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      {...props}
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
  );
};

const SatelliteTileLayer = (props: LayerProps) => {
  return (
    <TileLayer
      attribution="&copy; Esri"
      {...props}
      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    />
  );
};

const DarkThemedTileLayer = (props: LayerProps) => {
  return <OpenFreeMapLayer styleUrl="https://tiles.openfreemap.org/styles/dark" {...props} />;
};

const TopographicTileLayer = (props: LayerProps) => {
  return (
    <TileLayer
      attribution="&copy; OpenTopoMap contributors"
      {...props}
      url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
    />
  );
};
